import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import styles from './ShippingQueue.module.css';
import { getShippingQueue, addSampleShipment } from '../../services/shippingService';
import ShipArtworkModal from '../ShipArtworkModal';

const ShippingQueue = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isDevelopment = import.meta.env.DEV;

  const fetchQueue = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    try {
      const data = await getShippingQueue();
      setQueue(data);
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch shipping queue';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching shipping queue:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleAddSample = async () => {
    try {
      await addSampleShipment();
      toast.success('Sample shipment added successfully');
      fetchQueue(false); // Refresh without full loading state
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to add sample shipment';
      toast.error(errorMessage);
      console.error('Error adding sample data:', err);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading shipping queue...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <p className={styles.errorMessage}>{error}</p>
        <button 
          onClick={() => fetchQueue()} 
          className={styles.retryButton}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.queue}>
      <div className={styles.queueHeader}>
        <h2>Shipping Queue</h2>
        <button 
          onClick={() => fetchQueue(false)}
          className={styles.refreshButton}
          disabled={isRefreshing}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {isDevelopment && (
        <button 
          onClick={handleAddSample}
          className={styles.sampleButton}
        >
          Add Sample Shipment
        </button>
      )}

      {queue.length === 0 ? (
        <div className={styles.empty}>
          <p>No orders to fulfill</p>
          <p className={styles.emptySubtext}>New orders will appear here when they're ready to ship</p>
        </div>
      ) : (
        <div className={styles.list}>
          {queue.map((item) => (
            <div key={item.artwork_id + '-' + item.shopify_order_id} className={styles.label}>
              <div className={styles.labelHeader}>
                <h3>{item.title}</h3>
                <span className={styles.status}>Unfulfilled</span>
              </div>
              <div className={styles.labelDetails}>
                <p>Order ID: {item.shopify_order_id}</p>
                <p>Order Date: {item.order_date ? format(new Date(item.order_date), 'MMM d, yyyy') : 'N/A'}</p>
                <p>Amount: ${item.amount}</p>
                {/* Optionally show buyer info if available */}
                {/* <p>Buyer: {item.buyer_id}</p> */}
                {item.image_url && (
                  <img 
                    src={item.image_url} 
                    alt={item.title} 
                    className={styles.artworkImage}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder-artwork.png';
                    }}
                  />
                )}
              </div>
              <button 
                className={styles.createShipmentButton} 
                onClick={() => {
                  console.log('Opening ShipArtworkModal with shipment:', item);
                  setSelectedShipment(item);
                }}
              >
                Ship This Order
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedShipment && (
        <ShipArtworkModal 
          shipment={selectedShipment} 
          onClose={() => setSelectedShipment(null)}
          onSuccess={() => {
            toast.success('Shipping label created successfully');
            fetchQueue(false);
          }}
        />
      )}
    </div>
  );
};

export default ShippingQueue; 